import { t } from "@lingui/macro";
import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { SharedValue } from "react-native-reanimated";
import { useFragment } from "react-relay";

import { MobileHeader } from "@whoosh/design-system/src/patterns/patronApp/MobileHeader/MobileHeader";
import { getThumbnail } from "@whoosh/utilities/src/Cloudinary";
import { NumericalDayOfWeek } from "@whoosh/utilities/src/calendars";

import { usePatronTopbarMobileHeaderNav } from "../../../../components/PageWrapper/hooks/usePatronTopbarMobileHeaderNav";
import { ClubStackNavigatorParamList } from "../../../../navigators/ClubNavigator";
import {
  selectedPartySize_atom,
  selectedSeatingShift_atom,
} from "../../../../screens/BookingDetail/ForYouDiningRemains/atoms";
import {
  isBookingMultipleTeeTimesAtom,
  teeSheetGroupSizeFilterAtom,
  teeSheetHoleFilterAtom,
  teeSheetListFilterAtom,
  teeSheetTimesAtom,
} from "../../atoms";
import { TeeSheetAgendaHeaderMobileNewFragment_club$key } from "./__generated__/TeeSheetAgendaHeaderMobileNewFragment_club.graphql";
import { groupSizeLabel, holeCountLabel, timeRangeLabel } from "./utils";

type TeeSheetSheetAgendaHeaderMobileNewProps = {
  onBackPress?: () => void;
  isScrolled: boolean;
  onFilterPress: () => void;
  onWaitlistPress?: () => void;
  title: string;
  courseFilterLabel?: string | null;
  variant?: "GOLF_COURSE" | "MULTI_SLOT" | "DINING";
  clubRef: TeeSheetAgendaHeaderMobileNewFragment_club$key;
  scrollY?: SharedValue<number>;
  startDate?: string;
  endDate?: string;
  selectedDate?: string;
  onDateChange?: (date: string) => void;
  clubWeekStartsOn?: NumericalDayOfWeek;
  showWaitlist?: boolean;
};

export const TeeSheetAgendaHeaderMobileNew = ({
  onFilterPress,
  onWaitlistPress,
  onBackPress,
  courseFilterLabel,
  scrollY,
  clubRef,
  startDate,
  endDate,
  showWaitlist,
  selectedDate,
  onDateChange,
  clubWeekStartsOn,
  variant,
}: TeeSheetSheetAgendaHeaderMobileNewProps) => {
  const { facilitySlug } =
    useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>().params;
  const club = useFragment(
    TeeSheetAgendaHeaderMobileNewFragmentString,
    clubRef
  );

  const teeSheetListFilter = useAtomValue(teeSheetListFilterAtom);
  const teeSheetGroupSizeFilter = useAtomValue(teeSheetGroupSizeFilterAtom);
  const teeSheetHoleFilter = useAtomValue(teeSheetHoleFilterAtom);
  const teeSheetTimes = useAtomValue(teeSheetTimesAtom);
  const isBookingMultipleTeeTimes = useAtomValue(isBookingMultipleTeeTimesAtom);
  const selectedPartySize = useAtomValue(selectedPartySize_atom);
  const selectedSeatingShift = useAtomValue(selectedSeatingShift_atom);

  const isPrivate = club?.supportsPrivate;
  const isDining = variant === "DINING";

  const filtersBarValues = isDining
    ? [
        {
          value: groupSizeLabel(selectedPartySize),
          placeholder: t`No. of people`,
        },
        {
          value: selectedSeatingShift?.name ?? null,
          placeholder: t`Seating`,
        },
      ]
    : [
        {
          value: timeRangeLabel(teeSheetTimes),
          placeholder: t`Time of day`,
        },
        // Only show this filter if club is private (supportsPrivate)
        ...(isPrivate
          ? [
              {
                value: teeSheetListFilter,
                placeholder: t`All times`,
              },
            ]
          : []),
        // Only show group size filter if (isPrivate && teeSheetListFilter === "Available") || !isPrivate
        ...((isPrivate && teeSheetListFilter === "Available") || !isPrivate
          ? [
              {
                value: groupSizeLabel(teeSheetGroupSizeFilter),
                placeholder: t`No. of people`,
              },
            ]
          : []),
        {
          value: courseFilterLabel ?? null,
          placeholder: t`Course(s)`,
        },

        // Only show this filter if club is not private (does not support private)
        ...(!isPrivate
          ? [
              {
                value: holeCountLabel(teeSheetHoleFilter),
                placeholder: t`No. of holes`,
              },
            ]
          : []),
        ...(isBookingMultipleTeeTimes
          ? [
              {
                value: t`Multiple Times`,
                placeholder: t`Multiple Times`,
              },
            ]
          : []),
      ];

  const shouldShowDateSwitcher =
    startDate &&
    endDate &&
    selectedDate &&
    onDateChange &&
    clubWeekStartsOn !== undefined;

  const selectedFacilityDisplayName = club?.facilities?.edges?.find(
    (edge) => edge?.node?.slug === facilitySlug
  )?.node?.displayNameOrFacilityName;
  const headerFacilityName =
    selectedFacilityDisplayName ?? (isDining ? t`Dining` : "");

  const { customNavigationBar, navigationBarVariant } =
    usePatronTopbarMobileHeaderNav({
      leftAction: onBackPress ? "Back" : null,
      onLeftActionClick: onBackPress,
      title: headerFacilityName,
      subTitle: club?.name ?? "",
      clubLogoImageUrl: getThumbnail(
        club?.logoImage?.publicId ?? "",
        40,
        40
      )?.toURL(),
    });

  return (
    <MobileHeader
      customNavigationBar={customNavigationBar}
      navigationBarVariant={navigationBarVariant}
      filtersBar={{
        title: {
          leadingText: t`Find`,
          boldText: headerFacilityName,
          trailingText: t`Times`,
        },
        values: filtersBarValues,
        onPress: onFilterPress,
        rightAction: showWaitlist ? "Waitlist" : null,
        onRightActionClick: showWaitlist ? onWaitlistPress : undefined,
      }}
      dateSwitcher={
        shouldShowDateSwitcher
          ? {
              startDate: startDate,
              endDate: endDate,
              selectedDate: selectedDate,
              onChange: onDateChange,
              clubWeekStartsOn: clubWeekStartsOn,
            }
          : undefined
      }
      scrollY={scrollY}
    />
  );
};

export const TeeSheetAgendaHeaderMobileNewFragmentString = graphql`
  fragment TeeSheetAgendaHeaderMobileNewFragment_club on Club {
    name
    ...FacilityLogoFragment_club
    supportsPrivate
    logoImage {
      # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
      publicId: renderingKey
    }
    facilities {
      edges {
        node {
          id
          name
          displayNameOrFacilityName
          slug
        }
      }
    }
  }
`;
